import { applyStatusEffect } from '../ability/statuses/apply-status-effect';
import { record, type JanusCtx, type Row } from '../ability/context';
import { readInventoryField } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';
import { encounterField } from '../../shared/array-types';
import { appendSpawnStatus, type SpawnReaders, type SpawnRowPlan } from '../spawn/row-plan';

export const DUNGEON_LIGHT_STATUS = 'D9C8AD45-023D-0F08-18E1-12D6C21BF0E0';
export const LOCKED_STATUS = '477A7977-60D2-4B9C-96B0-6ED4BFD6437E';

export function initialRoomStatusIds(placement: Row): string[] {
    return [...(record(placement.lightFixture)?.part === 'torch' ? [DUNGEON_LIGHT_STATUS] : []),
        ...(placement.startsLocked === true ? [LOCKED_STATUS] : [])];
}

/** The generator decides initial effects before submitting its birth tree. No held entity is cloned. */
export function appendInitialInventoryStatus(plan: SpawnRowPlan, source: SpawnReaders, encounter: Row, itemId: string): void {
    const root = plan.writes.find(write => write.table === 'encounter_inventory');
    if (!root) throw new Error('room-generation:initial-status-plan-unavailable');
    let definition: Row;
    try { definition = source.item(itemId); }
    catch { throw new Error('status-admission:definition-unavailable'); }
    if (!definition || String(readItemField(definition, 'item_id') ?? '').toUpperCase() !== itemId.toUpperCase())
        throw new Error('status-admission:definition-unavailable');
    const targetId = String(readInventoryField({ inventory: root.row }, 'inventory_id'));
    const origin = String(readInventoryField({ inventory: root.row }, 'inventory_item_id'));
    const target = { ...source.item(origin), inventory: root.row };
    const casterId = String(encounterField(encounter, 'encounter_id'));
    const caster = { encounters: { encounter_id: casterId } };
    const unavailable = (): never => { throw new Error('room-generation:initial-status-plan-unavailable'); };
    const ctx: JanusCtx = {
        encounter, members: new Map(), originals: new Map(), read: {
            value: (row, key) => row ? source.value(row, key) : null,
            has: (row, key) => row ? source.entries(row, key).length > 0 : false,
            entries: (row, key) => row ? source.entries(row, key) : [],
            definition: id => { try { return source.item(id); } catch { return null; } },
            statuses: () => [], inventoryField: readInventoryField, uuid: source.uuid,
            inCombat: () => false, campaignClock: () => null, addCampaignSeconds: unavailable,
            visibility: () => null, reactionInventoryComplete: () => false, deadState: () => null,
        }, roll: { d: unavailable, fraction: unavailable }, rows: [], statusesAdded: [], statusesRemoved: [],
        statusStacks: [], chargesChanged: [], inventoryChanged: [], statusConnectedId: targetId, rollPrivate: 0,
    };
    applyStatusEffect(ctx, { casterId, targetIds: [targetId], abilityId: itemId, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
        useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' }, {}, itemId,
    'status_effect_applied', caster, target, { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, false, false, 0, false, origin);
    if (ctx.statusesAdded.length !== 1 || ctx.statusesRemoved.length || ctx.rows.length)
        throw new Error('room-generation:initial-status-plan-unavailable');
    appendSpawnStatus(plan, source, targetId, casterId, itemId, origin,
        { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0], connection: targetId });
    const row = plan.writes[plan.writes.length - 1]!.row, added = ctx.statusesAdded[0]!;
    for (const [key, value] of Object.entries(added)) if (key.startsWith('status_') && key !== 'status_id') row[key] = value;
    delete row.status_private; delete row.status_deleted; delete row.status_campaign_id;
}
